<template>
    <div class="dropdown-container">
        <div ref="activatorWrapper" class="dropdown-activator" @click="onActivatorClick">
            <slot name="activator" :is-open="isOpen" />
        </div>

        <teleport to="body">
            <div
                v-if="isOpen"
                ref="dropdown"
                v-bind="$attrs"
                class="dropdown-menu"
                :class="[hasShadow && 'has-shadow', hasBorder && 'has-border', hasNoPadding && 'has-no-padding', dropdownClass]"
                :style="computedTeleportStyle"
            >
                <slot v-if="$slots.items" name="items" :on-close="close" />
                <template v-else-if="items?.length">
                    <DropdownMenuItem
                        v-for="(item, index) in items"
                        :key="index"
                        :action-type="item.actionType"
                        :text="item.text"
                        :icon="item.icon"
                        :size="itemsSize"
                        :type="item.type"
                        :img-url="item.imgUrl"
                        :alt="item.alt"
                        :help-text="item.helpText"
                        :to="item.to"
                        :is-external="item.isExternal"
                        :has-separator="item.hasSeparator"
                        @click="handleItemClick(item.callback)"
                    />
                </template>
            </div>
        </teleport>
    </div>
</template>

<script lang="ts">
import { defineComponent, PropType, nextTick } from "vue";
import DropdownMenuItem from "./DropdownMenuItem.vue";

import type { DropdownItemSize, DropdownPosition, DropdownMenuItemData } from "@/types/dropdowns";

const DROPDOWN_OPEN_EVENT = "dropdown-menu-open";

export default defineComponent({
    components: {
        DropdownMenuItem,
    },
    name: "DropdownMenu",
    inheritAttrs: false,

    props: {
        items: {
            type: Array as PropType<DropdownMenuItemData[]>,
            default: () => [],
        },
        itemsSize: {
            type: String as PropType<DropdownItemSize>,
            default: "md",
        },
        width: {
            type: Number,
            default: 180,
        },
        position: {
            type: String as PropType<DropdownPosition>,
            default: "bottom-right",
        },
        positionXOffset: {
            type: [Number, String],
            default: 0,
        },
        positionYOffset: {
            type: [Number, String],
            default: 0,
        },
        hasShadow: {
            type: Boolean,
            default: true,
        },
        hasBorder: {
            type: Boolean,
            default: true,
        },
        dropdownClass: {
            type: String,
            default: "",
        },
        avoidClosingOnScroll: {
            type: Boolean,
            default: false,
        },
        hasNoPadding: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            isOpen: false,
            isPositioned: false,
            activatorRect: null as DOMRect | null,
            dropdownRect: null as DOMRect | null,
        };
    },

    computed: {
        computedTeleportStyle(): Record<string, string> {
            if (!this.isPositioned || !this.activatorRect || !this.dropdownRect) {
                return {
                    position: "absolute",
                    top: "0px",
                    left: "0px",
                    visibility: "hidden",
                };
            }

            const a = this.activatorRect;
            const dHeight = this.dropdownRect.height;
            const dWidth = this.width;

            const x = Number(this.positionXOffset) || 0;
            const y = Number(this.positionYOffset) || 0;

            const [primary, secondary] = this.position.split("-");

            let top = 0;
            let left = 0;

            // ── PRIMARY ──
            switch (primary) {
                case "bottom":
                    top = a.bottom + y;
                    break;
                case "top":
                    top = a.top - dHeight - y;
                    break;
                case "left":
                    left = a.left - dWidth - x;
                    break;
                case "right":
                    left = a.right + x;
                    break;
            }

            // ── SECONDARY ──
            switch (secondary) {
                case "left":
                    left = a.left;
                    break;
                case "right":
                    left = a.right - dWidth;
                    break;
                case "top":
                    top = a.top;
                    break;
                case "bottom":
                    top = a.bottom - dHeight;
                    break;
            }

            return {
                position: "absolute",
                top: `${top + window.scrollY}px`,
                left: `${left + window.scrollX}px`,
                width: `${dWidth}px`,
                visibility: "visible",
                zIndex: "50",
            };
        },
    },

    methods: {
        getActivatorElement: function () {
            const wrapper = this.$refs.activatorWrapper as HTMLElement | undefined;
            if (!wrapper) return null;

            const button = wrapper.querySelector("button");
            if (button) return button;

            const roleButton = wrapper.querySelector('[role="button"]');
            if (roleButton) return roleButton as HTMLElement;

            return wrapper;
        },

        onActivatorClick: function (event: MouseEvent) {
            const activatorEl = this.getActivatorElement();
            if (!activatorEl) return;

            if (activatorEl.contains(event.target as Node)) {
                this.toggle();
            }
        },

        toggle: function () {
            if (!this.isOpen) {
                window.dispatchEvent(new CustomEvent(DROPDOWN_OPEN_EVENT, { detail: this }));
            }

            this.isOpen = !this.isOpen;
            this.isPositioned = false;

            if (this.isOpen) {
                nextTick(() => {
                    requestAnimationFrame(() => {
                        this.updateRects();
                        this.isPositioned = true;
                    });
                });
            }
        },

        close: function () {
            this.isOpen = false;
            this.isPositioned = false;
        },

        onOtherDropdownOpen: function (event: Event) {
            if ((event as CustomEvent).detail !== this) {
                this.close();
            }
        },

        updateRects: function () {
            const activatorEl = this.getActivatorElement();
            const dropdownEl = this.$refs.dropdown as HTMLElement | undefined;

            if (!activatorEl || !dropdownEl) return;

            this.activatorRect = activatorEl.getBoundingClientRect();
            this.dropdownRect = dropdownEl.getBoundingClientRect();
        },

        handleItemClick: function (callback?: () => void) {
            if (callback) {
                callback();
            }
            this.close();
        },

        handleClickOutside: function (event: MouseEvent) {
            if (!this.isOpen) return;

            const activatorEl = this.getActivatorElement();
            const dropdownEl = this.$refs.dropdown as HTMLElement;

            if (activatorEl && dropdownEl && !activatorEl.contains(event.target as Node) && !dropdownEl.contains(event.target as Node)) {
                this.close();
            }
        },

        onScroll: function () {
            if (!this.avoidClosingOnScroll) {
                this.close();
            }
        },
    },

    mounted() {
        document.addEventListener("click", this.handleClickOutside);
        window.addEventListener("scroll", this.onScroll, true);
        window.addEventListener("resize", this.close);
        window.addEventListener(DROPDOWN_OPEN_EVENT, this.onOtherDropdownOpen);
    },

    beforeUnmount() {
        document.removeEventListener("click", this.handleClickOutside);
        window.removeEventListener("scroll", this.onScroll, true);
        window.removeEventListener("resize", this.close);
        window.removeEventListener(DROPDOWN_OPEN_EVENT, this.onOtherDropdownOpen);
    },
});
</script>

<style scoped>
@import "@/style/components/dropdowns.css";
</style>
